import { formatMoney } from "../helpers/currency";
import { ProductMediaSlider, moveToSlide } from "./product-media-slider";

export interface LiquidProduct {
  id: number;
  title: string;
  vendor: string;
  name: string;
  featured_image: string;
  images: { src: string }[];
  variants: LiquidVariant[];
  handle: string;
}

export interface LiquidVariant {
  id: string;
  description: string;
  options: string[];
  price: string;
  compare_at_price: string | null;
  featured_media: {
    position: number;
  };
  title: string;
  available: boolean;
}

interface IProduct extends HTMLElement {
  selectedVariantId: string | null;
  setSelectedVariantId(variantId: string): void;
}

export class Product extends HTMLElement implements IProduct {
  private _selectedVariant: LiquidVariant | null = null;

  private _product: LiquidProduct | null = null;

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ["selected-variant-id"];
  }

  connectedCallback() {
    this.loadProductData();
    this.setupEventListeners();
    this.stickyAddToCart();
    /**
     * We need to save the product handle in sessionStorage so we can scroll
     * to the product card when the user navigates back from the product page
     * to the collection page.
     */
    this.saveProductHandleInSessionStorage();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    if (name === "selected-variant-id" && oldValue !== newValue) {
      this.updateSelectedVariant(newValue);
      this.updateProductDetails();
    }
  }

  get selectedVariantId(): string | null {
    return this.getAttribute("selected-variant-id");
  }

  set selectedVariantId(value: string | null) {
    if (value !== null) {
      this.setAttribute("selected-variant-id", value);
    } else {
      this.removeAttribute("selected-variant-id");
    }
  }

  public setSelectedVariantId(variantId: string): void {
    this.selectedVariantId = variantId;
  }

  get selectedVariant(): LiquidVariant | null {
    return this._selectedVariant;
  }

  private loadProductData(): void {
    const productJson = this.getAttribute("product");
    if (productJson) {
      try {
        this._product = JSON.parse(productJson);
      } catch (e) {
        console.error("Error parsing product data:", e);
      }
    } else {
      console.error("Product attribute is missing or empty");
    }
  }

  private updateSelectedVariant(variantId: string | null): void {
    if (!this._product || !this._product.variants) {
      console.error("No product or variants available");
      return;
    }
    const variant = this._product.variants.find(
      (v) => String(v.id) === variantId,
    );
    if (variant) {
      this._selectedVariant = variant;
    } else {
      console.error("Variant with id", variantId, "not found");
    }
  }

  private updateProductDetails(): void {
    if (!this._selectedVariant) {
      console.error("No selected variant available");
      return;
    }

    // Target spans to update product details based on selected variant
    const { price, compare_at_price, featured_media, available } =
      this._selectedVariant;
    const variantImageIndex = featured_media.position - 1;

    const productDetails = {
      productMediaSlider: this.querySelector(
        "uwp-product-media-slider",
      ) as ProductMediaSlider,
      price: this.querySelector("[data-price]"),
      comparePrice: this.querySelector("[data-compare-at-price]"),
      addToCartButton: this.querySelector("[data-add-to-cart]"),
    };

    if (productDetails.price) {
      productDetails.price.textContent = formatMoney(price);
    }

    if (productDetails.comparePrice) {
      productDetails.comparePrice.textContent = compare_at_price
        ? formatMoney(compare_at_price)
        : "";
    }

    if (productDetails.productMediaSlider) {
      moveToSlide(productDetails.productMediaSlider, variantImageIndex);
    }

    if (productDetails.addToCartButton) {
      productDetails.addToCartButton.toggleAttribute("disabled", !available);
    }
  }
  private setupEventListeners() {
    const atcButton = document.querySelector<HTMLButtonElement>("#atcButton");
    const variantButtons =
      this.querySelectorAll<HTMLButtonElement>("[data-qty-button]");

    const inputs = this.querySelectorAll<HTMLInputElement>("[data-qty-input]");

    if (atcButton) {
      atcButton.addEventListener("click", () => {
        inputs.forEach((input) => {
          input.value = "0";
          input.dispatchEvent(new Event("change"));
        });
      });
    }

    variantButtons.forEach((button) => {
      button.addEventListener("click", () => {
        this.updateTotalPrice(inputs);
        const noQuantityParent = button.closest(
          ".product-swatches--no-quantity",
        );
        if (noQuantityParent) {
          noQuantityParent.classList.remove("product-swatches--no-quantity");
        }
      });
    });
  }

  private updateTotalPrice(
    inputs: HTMLInputElement[] | NodeListOf<HTMLInputElement>,
  ) {
    let totalPrice = 0;

    inputs.forEach((input) => {
      const quantity = parseInt(input.value, 10);
      const price = parseFloat(input.dataset.price || "0");

      if (!isNaN(quantity) && !isNaN(price) && quantity > 0) {
        totalPrice += quantity * price;
      }
    });

    this.displayTotalPrice(totalPrice);
  }

  private displayTotalPrice(totalPrice: number) {
    const atcButtonTotal = this.querySelector("[data-product-total]");
    if (atcButtonTotal && totalPrice > 0) {
      atcButtonTotal.innerHTML = `<span class="atc-breaker">|</span>${formatMoney(totalPrice)}`;
    } else if (atcButtonTotal) {
      atcButtonTotal.innerHTML = "";
    }
  }

  private stickyAddToCart() {
    const atcButtonWrapper =
      this.querySelector<HTMLButtonElement>("[js-atc-wrapper]");
    const atcVisibleDiv = this.querySelector("[js-atc-visible]") as HTMLElement;

    if (atcButtonWrapper && atcVisibleDiv) {
      // clone it and insert it after the original
      const clonedButton = atcButtonWrapper.cloneNode(true) as HTMLElement;
      clonedButton.classList.add("placeholder", "hide");
      atcButtonWrapper.after(clonedButton);
      window.onscroll = () => {
        if (window.innerWidth < 768) {
          const sticky = atcVisibleDiv.offsetTop;
          // Workaround for the chat button overlaping the sticky add to cart button
          const chatButton = document.getElementById("CookiebotWidget");
          if (chatButton) {
            chatButton.style.transition = "bottom .3s ease";
          }
          if (window.scrollY > sticky) {
            atcButtonWrapper.classList.add("sticky");
            clonedButton.classList.remove("hide");
            chatButton?.style.setProperty("bottom", "70px");
          } else {
            atcButtonWrapper.classList.remove("sticky");
            clonedButton.classList.add("hide");
            chatButton?.style.setProperty("bottom", "10px");
          }
        }
      };
    }
  }

  private saveProductHandleInSessionStorage(): void {
    const previousUrl = document.referrer;
    // Don't save the product handle if the user is coming from a non-collection page
    if (!previousUrl.includes("/collections/")) {
      localStorage.removeItem("productHandle");
      return;
    }
    const productHandle = this._product?.handle;
    if (productHandle) {
      sessionStorage.setItem("productHandle", productHandle);
    }
  }
}

customElements.define("uwp-product", Product);
